import { useIsMutating } from "@tanstack/react-query";
import { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import StepperVertical from "@krea/customer-features/shared-components/stepper-vertical";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { SUBMIT_APPLICATION_STORE_KEY } from "@krea/customer-features/store/queryClient";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";

interface ILoanApplicationFlowDescriptionProps {
  conditions?: [
    {
      done: boolean;
      active: boolean;
    },
    {
      done: boolean;
      active: boolean;
    },
    {
      done: boolean;
      active: boolean;
    },
    {
      done: boolean;
      active: boolean;
    },
  ];
  useConditions?: boolean;
}

const LoanApplicationFlowDescription = ({
  conditions,
  useConditions = false,
}: ILoanApplicationFlowDescriptionProps) => {
  const { t } = useTranslation();
  const submittingMutations = useIsMutating({
    mutationKey: [SUBMIT_APPLICATION_STORE_KEY],
  });
  const { data: { state } = {} } = useGetCachedCurrentLoanApplication();

  const isApplicationSent = () => {
    return (
      state === LoanApplicationStates.CREATED ||
      state === LoanApplicationStates.ADVICE ||
      state === LoanApplicationStates.OPEN_FOR_BIDS
    );
  };

  const isSubmitting = submittingMutations > 0;

  const stepsTitles = [
    t("iaq.applicationPreview.steps.common.reviewAndSubmit"),
    t("iaq.applicationPreview.steps.common.creditCheck"),
    t("iaq.applicationPreview.steps.common.improveApplication"),
    t("iaq.applicationPreview.steps.common.receiveFunds"),
  ];

  const steps: ComponentProps<typeof StepperVertical>["steps"] = [
    {
      title: stepsTitles[0],
      active: state === LoanApplicationStates.DRAFT && !isSubmitting,
      done: useConditions
        ? conditions?.[0].done
        : isSubmitting || isApplicationSent(),
    },
    {
      title: stepsTitles[1],
      active: state === LoanApplicationStates.DRAFT && isSubmitting,
      done: isApplicationSent(),
    },
    {
      title: stepsTitles[2],
      active:
        state === LoanApplicationStates.ADVICE ||
        state === LoanApplicationStates.OPEN_FOR_BIDS,
      done: false,
    },
    {
      title: stepsTitles[3],
      active: false,
      done: false,
    },
  ];

  const conditionSteps = stepsTitles.map((title, index) => ({
    title,
    active: conditions?.[index].active,
    done: conditions?.[index].done,
  }));

  return <StepperVertical steps={useConditions ? conditionSteps : steps} />;
};

export default LoanApplicationFlowDescription;
